/**
 * Privacy use cases: owner account deletion (grace period + immediate), owner data export (JSON + CSV + ZIP),
 * and client self-service erasure through the manage link. Required by Apple 5.1.1(v), Google Play and Israeli privacy law (amendment 13).
 */
import { buildZip, nis, toCsv } from '../export';
import { dayKeyOf, toMs, DAY } from '../time';
import {
  fail, ok, type Appointment, type Business, type Client, type ConsentRecord, type Id, type IsoUtc, type Message, type Owner, type Payment, type Result,
  type Service, type Settings, type Staff, type TimeOff, type WaitlistEntry, type WeeklyHours,
} from '../types';
import type { TemplateCall } from '../../adapters/messaging/templates';
import { clientUseCases } from './clients';
import { loadBundle, planText, type Deps } from './context';
import { cancelWithoutFee, DELETION_GRACE_DAYS, DELETION_POLICY, isFutureLive, purgeBusiness } from './erasure';

export { DELETION_GRACE_DAYS, DELETION_POLICY } from './erasure';

export interface DeletionPreview {
  graceDays: number;
  /** Live clients (the rows that will be erased). */
  clients: number;
  /** Future appointments that will be cancelled, soonest first. */
  appointments: Array<{ appointmentId: Id; start: IsoUtc; clientName: string; serviceName: string; depositPaid: boolean }>;
  /** Clients who will get the closure message (one per distinct client). */
  clientsToNotify: number;
  retained: readonly string[];
}
export interface DeletionOutcome {
  immediate: boolean;
  /** Absent for an immediate delete. */
  scheduledFor?: IsoUtc;
  cancelledAppointments: number;
  notifiedClients: number;
  retained: readonly string[];
  /** Rows removed per table (immediate delete only). */
  purged?: Record<string, number>;
}
export interface ExportFile { name: string; mime: string; content: string }
export interface OwnerExportJson {
  format: 'planella-owner-export';
  version: 1;
  exportedAt: IsoUtc;
  sensitiveNotesIncluded: boolean;
  business: Business;
  owner?: Owner;
  settings: Settings;
  staff: Staff[];
  services: Service[];
  weeklyHours: WeeklyHours[];
  timeOff: TimeOff[];
  clients: Client[];
  appointments: Array<Omit<Appointment, 'manageToken'>>;
  payments: Payment[];
  waitlistEntries: WaitlistEntry[];
  consents: ConsentRecord[];
  messages: Message[];
}
export interface OwnerExport {
  exportedAt: IsoUtc;
  json: OwnerExportJson;
  /** planella-export.json plus clients.csv, appointments.csv, payments.csv, services.csv, settings.csv. */
  files: ExportFile[];
  /** All files in one ZIP (planella-export-YYYY-MM-DD.zip). */
  zip: Uint8Array;
  zipName: string;
  counts: { clients: number; appointments: number; payments: number };
}
export interface MyDataPreview {
  businessName: string;
  upcoming: Array<{ start: IsoUtc; serviceName: string }>;
}

export function privacyUseCases(d: Deps) {
  const clients = clientUseCases(d);

  async function previewAccountDeletion(businessId: Id): Promise<Result<DeletionPreview, 'business_not_found'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const nowMs = d.clock.now();
    const appts = (await d.storage.repo('appointments').list({ where: { businessId } })).filter((a) => isFutureLive(a, nowMs)).sort((x, y) => x.start.localeCompare(y.start));
    const all = await d.storage.repo('clients').list({ where: { businessId } });
    const byId = new Map(all.map((c) => [c.id, c]));
    return ok({
      graceDays: DELETION_GRACE_DAYS,
      clients: all.filter((c) => !c.deletedAt).length,
      appointments: appts.map((a) => ({
        appointmentId: a.id, start: a.start, clientName: byId.get(a.clientId)?.name ?? '', serviceName: b.services.find((s) => s.id === a.serviceId)?.name ?? '',
        depositPaid: a.depositStatus === 'paid',
      })),
      clientsToNotify: new Set(appts.map((a) => a.clientId).filter((id) => byId.get(id) && !byId.get(id)!.deletedAt)).size,
      retained: DELETION_POLICY.retained,
    });
  }

  /**
   * Owner deletes the account and the business. Default: closes the page to clients at once, cancels and notifies every future
   * appointment, and hard-deletes everything DELETION_GRACE_DAYS later (the scheduler does it); signing in during that time
   * shows a banner and `cancelOwnerDeletion` reopens the account (cancelled appointments stay cancelled). `immediate: true` deletes now.
   * Closure messages are service messages about the client's own booking: sent at once, regardless of marketing consent, plan caps and quiet hours.
   */
  async function deleteOwnerAccount(input: { businessId: Id; immediate?: boolean }): Promise<Result<DeletionOutcome, 'business_not_found'>> {
    const b = await loadBundle(d, input.businessId);
    if (!b) return fail('business_not_found');
    const nowIso = d.clock.nowIso();
    const retained = DELETION_POLICY.retained;
    let cancelledAppointments = b.business.deletion?.cancelledAppointments ?? 0;
    let notifiedClients = b.business.deletion?.notifiedClients ?? 0;

    if (!b.business.deletion) {
      const text = planText(d, b);
      const notified = new Set<Id>();
      const live = (await d.storage.repo('appointments').list({ where: { businessId: input.businessId } })).filter((a) => isFutureLive(a, d.clock.now())).sort((x, y) => x.start.localeCompare(y.start));
      cancelledAppointments = 0;
      for (const a of live) {
        const done = await cancelWithoutFee(d, a.id, 'owner_closed');
        if (!done) continue;
        cancelledAppointments++;
        const client = await d.storage.repo('clients').get(a.clientId);
        const service = b.services.find((s) => s.id === a.serviceId);
        if (!client || client.deletedAt || !client.phone || !service) continue;
        const call: TemplateCall = { template: 'business_closed', params: {
          businessName: b.business.name, clientName: client.name.trim().split(/\s+/)[0] ?? client.name, serviceName: service.name,
          date: text.date(toMs(a.start)), time: text.time(toMs(a.start)),
        } };
        const sent = await d.messaging.send({ businessId: input.businessId, to: client.phone, call, clientId: client.id, appointmentId: a.id, purpose: 'reminders', dedupeKey: `business_closed:${a.id}` });
        if (d.messaging.dispatchNow) await d.messaging.dispatchNow(sent.id);
        notified.add(client.id);
      }
      notifiedClients = notified.size;
      const scheduledFor = new Date(d.clock.now() + DELETION_GRACE_DAYS * DAY).toISOString();
      await d.storage.repo('businesses').put({ ...b.business, deletion: { requestedAt: nowIso, scheduledFor, cancelledAppointments, notifiedClients }, updatedAt: nowIso });
    }

    if (input.immediate) {
      const session = d.auth.getSession();
      const purged = await purgeBusiness(d, input.businessId);
      if (session?.businessId === input.businessId) d.auth.signOut();
      return ok({ immediate: true, cancelledAppointments, notifiedClients, retained, purged });
    }
    const cur = await d.storage.repo('businesses').get(input.businessId);
    return ok({ immediate: false, scheduledFor: cur?.deletion?.scheduledFor ?? nowIso, cancelledAppointments, notifiedClients, retained });
  }

  /** "ביטול מחיקה": reopens the page. Appointments cancelled by the request stay cancelled (their clients were told). */
  async function cancelOwnerDeletion(businessId: Id): Promise<Result<{ cancelledAppointments: number }, 'business_not_found' | 'not_scheduled'>> {
    const business = await d.storage.repo('businesses').get(businessId);
    if (!business) return fail('business_not_found');
    if (!business.deletion) return fail('not_scheduled');
    const { deletion, ...rest } = business;
    await d.storage.repo('businesses').put({ ...rest, updatedAt: d.clock.nowIso() });
    return ok({ cancelledAppointments: deletion.cancelledAppointments });
  }

  /** Called by the scheduler: hard-deletes every account whose grace period is over. Returns how many. */
  async function purgeDueAccounts(): Promise<number> {
    const due = await d.storage.repo('businesses').list({ filter: (b) => b.deletion !== undefined && toMs(b.deletion.scheduledFor) <= d.clock.now() });
    for (const b of due) {
      const session = d.auth.getSession();
      await purgeBusiness(d, b.id);
      if (session?.businessId === b.id) d.auth.signOut();
    }
    return due.length;
  }

  // ------------------------------------------------------------ export

  async function exportOwnerData(businessId: Id, opts: { includeSensitive?: boolean } = {}): Promise<Result<OwnerExport, 'business_not_found'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const where = { businessId };
    const exportedAt = d.clock.nowIso();
    const includeSensitive = opts.includeSensitive === true;
    const [owner] = await d.storage.repo('owners').list({ where });
    const clientRows = (await d.storage.repo('clients').list({ where })).sort((a, c) => a.createdAt.localeCompare(c.createdAt))
      .map((c) => (includeSensitive ? c : { ...c, sensitiveNotes: '' }));
    const appts = (await d.storage.repo('appointments').list({ where })).sort((a, c) => a.start.localeCompare(c.start));
    const payments = await d.storage.repo('payments').list({ where });
    const json: OwnerExportJson = {
      format: 'planella-owner-export', version: 1, exportedAt, sensitiveNotesIncluded: includeSensitive,
      business: b.business, ...(owner ? { owner } : {}), settings: b.settings, staff: b.staff, services: b.services, weeklyHours: b.weeklyHours, timeOff: b.timeOff,
      clients: clientRows,
      appointments: appts.map(({ manageToken: _t, ...rest }) => rest),
      payments,
      waitlistEntries: await d.storage.repo('waitlistEntries').list({ where }),
      consents: await d.storage.repo('consents').list({ where }),
      // Message bodies carry personal manage/pay links (secret tokens): the export keeps the words, not the working links.
      messages: (await d.storage.repo('messages').list({ where })).map((m) => ({
        ...m, params: Object.fromEntries(Object.entries(m.params).map(([k, v]) => [k, /^https?:\/\//.test(v) ? '[link]' : v])),
        ...(m.text !== undefined ? { text: m.text.replace(/https?:\/\/\S+/g, '[link]') } : {}),
      })),
    };
    const clientById = new Map(clientRows.map((c) => [c.id, c]));
    const serviceName = (id: Id): string => b.services.find((s) => s.id === id)?.name ?? '';
    const staffName = (id: Id): string => b.staff.find((s) => s.id === id)?.name ?? '';
    const csv = (name: string, header: string[], rows: (string | number | boolean | undefined)[][]): ExportFile => ({ name, mime: 'text/csv;charset=utf-8', content: toCsv(header, rows) });
    const settingsRows: [string, string][] = [];
    const flat = (prefix: string, v: unknown): void => {
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) for (const [k, x] of Object.entries(v)) flat(prefix ? `${prefix}.${k}` : k, x);
      else settingsRows.push([prefix, Array.isArray(v) ? JSON.stringify(v) : String(v ?? '')]);
    };
    flat('business.name', b.business.name); flat('business.slug', b.business.slug); flat('business.profession', b.business.profession); flat('business.plan', b.business.plan);
    flat('business.phone', b.business.phone); flat('business.whatsapp', b.business.whatsapp); flat('business.address', b.business.address);
    const { id: _i, businessId: _b, createdAt: _c, updatedAt: _u, ...settingsOnly } = b.settings;
    flat('settings', settingsOnly);
    const files: ExportFile[] = [
      { name: 'planella-export.json', mime: 'application/json', content: JSON.stringify(json, null, 2) },
      csv('clients.csv', ['id', 'name', 'phone', 'work_notes', ...(includeSensitive ? ['sensitive_notes'] : []), 'tags', 'created_at', 'erased'],
        clientRows.map((c) => [c.id, c.name, c.phone, c.workNotes, ...(includeSensitive ? [c.sensitiveNotes] : []), c.tags.join(' | '), c.createdAt, !!c.deletedAt])),
      csv('appointments.csv', ['id', 'start', 'end', 'status', 'service', 'staff', 'client', 'phone', 'price_nis', 'deposit_nis', 'deposit_status', 'paid_nis', 'payment_method', 'fee_nis', 'cancel_reason', 'source', 'note', 'created_at'],
        appts.map((a) => [a.id, a.start, a.end, a.status, serviceName(a.serviceId), staffName(a.staffId), clientById.get(a.clientId)?.name ?? '', clientById.get(a.clientId)?.phone ?? '',
          nis(a.priceAgorot), nis(a.depositAgorot), a.depositStatus, nis(a.payment?.paidAgorot ?? a.paidAgorot), a.payment?.method ?? '', nis(a.feeAgorot), a.cancelReason ?? '', a.source, a.note ?? '', a.createdAt])),
      csv('payments.csv', ['id', 'appointment_id', 'amount_nis', 'refunded_nis', 'status', 'paid_at'], payments.map((p) => [p.id, p.appointmentId, nis(p.amountAgorot), nis(p.refundedAgorot), p.status, p.paidAt ?? ''])),
      csv('services.csv', ['id', 'name', 'category', 'duration_min', 'buffer_min', 'price_nis', 'deposit_nis', 'active'], b.services.map((s) => [s.id, s.name, s.category, s.durationMin, s.bufferMin, nis(s.priceAgorot), nis(s.depositAgorot), s.active])),
      csv('settings.csv', ['key', 'value'], settingsRows),
    ];
    const day = dayKeyOf(d.clock.now(), b.business.tz);
    const zipName = `planella-export-${day}.zip`;
    const zip = buildZip(files.map((f) => ({ name: f.name, data: f.content })), new Date(d.clock.now()));
    return ok({ exportedAt, json, files, zip, zipName, counts: { clients: clientRows.length, appointments: appts.length, payments: payments.length } });
  }

  // ------------------------------------------------------------ client self-service (manage link)

  async function appointmentByToken(token: string): Promise<{ appt: Appointment; client: Client } | undefined> {
    if (!token) return undefined;
    const [appt] = await d.storage.repo('appointments').list({ where: { manageToken: token } });
    const client = appt ? await d.storage.repo('clients').get(appt.clientId) : undefined;
    return appt && client && !client.deletedAt ? { appt, client } : undefined;
  }

  /** What "מחקו את הפרטים שלי" will do, for the confirmation sheet. */
  async function previewMyDataErasure(token: string): Promise<Result<MyDataPreview, 'not_found'>> {
    const found = await appointmentByToken(token);
    if (!found) return fail('not_found');
    const business = await d.storage.repo('businesses').get(found.appt.businessId);
    const services = await d.storage.repo('services').list({ where: { businessId: found.appt.businessId } });
    const nowMs = d.clock.now();
    const upcoming = (await d.storage.repo('appointments').list({ where: { clientId: found.client.id } })).filter((a) => isFutureLive(a, nowMs)).sort((x, y) => x.start.localeCompare(y.start));
    return ok({ businessName: business?.name ?? '', upcoming: upcoming.map((a) => ({ start: a.start, serviceName: services.find((s) => s.id === a.serviceId)?.name ?? '' })) });
  }

  /** The client erases their own details at this business: future bookings are cancelled (no fee), the record is anonymised, the link stops working. */
  async function eraseMyData(token: string): Promise<Result<{ cancelledAppointments: number }, 'not_found'>> {
    const found = await appointmentByToken(token);
    if (!found) return fail('not_found');
    const r = await clients.deleteClientData(found.client.id);
    return r.ok ? ok({ cancelledAppointments: r.value.cancelledAppointments }) : fail('not_found');
  }

  return { previewAccountDeletion, deleteOwnerAccount, cancelOwnerDeletion, purgeDueAccounts, exportOwnerData, previewMyDataErasure, eraseMyData };
}
